/**
 * AtRiskOKRList Component
 * Displays OKRs that are at risk of not completing
 *
 * Story 4.2: At-Risk OKR Identification
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, AlertCircle, Clock, ChevronDown, ChevronUp, User } from 'lucide-react';
import { Card } from '../../common';
import type { AtRiskOKRsData, AtRiskOKRItem, RiskLevel } from '../../../services/reportApi';

export interface AtRiskOKRListProps {
  /** At-risk OKRs data */
  data: AtRiskOKRsData;
  /** Loading state */
  isLoading?: boolean;
  /** Maximum items to show initially */
  initialLimit?: number;
  /** Click handler for OKR */
  onOKRClick?: (okrId: string) => void;
  /** Click handler for owner */
  onOwnerClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Get risk level styling
 */
function getRiskLevelStyle(level: RiskLevel): {
  bg: string;
  text: string;
  icon: React.ComponentType<{ className?: string }>;
} {
  switch (level) {
    case 'critical':
      return { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle };
    case 'high':
      return { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle };
    case 'medium':
      return { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock };
  }
}

/**
 * Progress bar component
 */
const ProgressBar: React.FC<{ progress: number; expected: number }> = ({ progress, expected }) => (
  <div className="relative w-full h-2 bg-stone-200 rounded-full overflow-hidden">
    {/* Expected progress marker */}
    <div
      className="absolute top-0 bottom-0 w-0.5 bg-indigo-500 z-10"
      style={{ left: `${Math.min(expected, 100)}%` }}
    />
    {/* Actual progress */}
    <div
      className={clsx(
        'h-full rounded-full transition-all',
        progress >= expected ? 'bg-green-500' :
        progress >= expected - 20 ? 'bg-amber-500' : 'bg-red-500'
      )}
      style={{ width: `${Math.min(progress, 100)}%` }}
    />
  </div>
);

/**
 * Loading skeleton
 */
const AtRiskSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-stone-200 rounded w-32" />
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-6 bg-stone-200 rounded w-20" />
        ))}
      </div>
    </div>
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 border border-stone-200 rounded-lg">
          <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
          <div className="h-4 bg-stone-200 rounded w-1/2 mb-3" />
          <div className="h-2 bg-stone-200 rounded w-full" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Individual OKR item component
 */
const OKRItem: React.FC<{
  okr: AtRiskOKRItem;
  onOKRClick?: (okrId: string) => void;
  onOwnerClick?: (userId: string) => void;
}> = ({ okr, onOKRClick, onOwnerClick }) => {
  const [expanded, setExpanded] = useState(false);
  const riskStyle = getRiskLevelStyle(okr.riskLevel);
  const RiskIcon = riskStyle.icon;

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden hover:border-stone-300 transition-colors">
      {/* Main content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Title */}
            <button
              onClick={() => onOKRClick?.(okr.okrId)}
              className="text-left font-medium text-stone-900 hover:text-indigo-600 transition-colors truncate block w-full"
            >
              {okr.title}
            </button>

            {/* Owner info */}
            <button
              onClick={() => onOwnerClick?.(okr.ownerId)}
              className="flex items-center gap-1 mt-1 text-sm text-stone-500 hover:text-stone-700 transition-colors"
            >
              <User className="w-3 h-3" />
              <span>{okr.ownerName}</span>
              {okr.department && (
                <span className="text-stone-400">({okr.department})</span>
              )}
            </button>
          </div>

          {/* Risk badge */}
          <span
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              riskStyle.bg,
              riskStyle.text
            )}
          >
            <RiskIcon className="w-3 h-3" />
            <span className="capitalize">{okr.riskLevel}</span>
          </span>
        </div>

        {/* Progress section */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-stone-600">
              Progress: <span className="font-medium">{okr.progress}%</span>
            </span>
            <span className="text-stone-500">
              Expected: {okr.expectedProgress}%
            </span>
          </div>
          <ProgressBar progress={okr.progress} expected={okr.expectedProgress} />
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {okr.daysRemaining > 0 ? `${okr.daysRemaining} days left` : 'Overdue'}
          </span>
          {okr.daysSinceLastUpdate > 0 && (
            <span>Last update: {okr.daysSinceLastUpdate}d ago</span>
          )}
        </div>

        {/* Expand button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 mt-3 text-sm text-indigo-600 hover:text-indigo-700"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show risk factors
            </>
          )}
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-stone-100 bg-stone-50">
          <h4 className="text-sm font-medium text-stone-700 mt-3 mb-2">Risk Factors</h4>
          <ul className="space-y-1">
            {okr.riskFactors.map((factor, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-stone-600">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <span>{factor}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-stone-200 text-sm">
            <span className="text-stone-500">Risk Score: </span>
            <span className="font-medium text-stone-900">{okr.riskScore.toFixed(0)}/100</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * At-Risk OKR List Component
 */
export const AtRiskOKRList: React.FC<AtRiskOKRListProps> = ({
  data,
  isLoading = false,
  initialLimit = 5,
  onOKRClick,
  onOwnerClick,
  className,
}) => {
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <AtRiskSkeleton />
      </Card>
    );
  }

  const { okrs, total, byCriticality } = data;
  const displayedOKRs = showAll ? okrs : okrs.slice(0, initialLimit);
  const hasMore = okrs.length > initialLimit;

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">At-Risk OKRs</h3>
          <p className="text-sm text-stone-500">{total} OKRs need attention</p>
        </div>

        {/* Criticality summary */}
        <div className="flex items-center gap-2">
          {byCriticality.critical > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              {byCriticality.critical} Critical
            </span>
          )}
          {byCriticality.high > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              {byCriticality.high} High
            </span>
          )}
          {byCriticality.medium > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              {byCriticality.medium} Medium
            </span>
          )}
        </div>
      </div>

      {/* OKR List */}
      {okrs.length === 0 ? (
        <div className="text-center py-8 text-stone-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No at-risk OKRs found</p>
          <p className="text-sm mt-1">All OKRs are on track!</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayedOKRs.map((okr) => (
              <OKRItem
                key={okr.okrId}
                okr={okr}
                onOKRClick={onOKRClick}
                onOwnerClick={onOwnerClick}
              />
            ))}
          </div>

          {/* Show more button */}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {showAll ? 'Show less' : `Show ${okrs.length - initialLimit} more`}
            </button>
          )}
        </>
      )}
    </Card>
  );
};

export default AtRiskOKRList;
